import { deleteSession } from '@/api/sender';
import { fetchSenderPresets, type SenderRunPreset } from '@/api/sender-presets';
import {
	archivePanelSenderSession,
	bulkSpamPanelSessions,
	exportPanelSenderSessions,
	fetchPanelSenderSessions,
	type PanelSenderSessionRow,
} from '@/api/sender-sessions';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { $features } from '@/entities/features-entity';
import { resolveSenderActionError } from '@/lib/resolve-sender-action-error';
import { senderRoutes } from '@/lib/sender-routes';
import { formatScenarioMeaning } from '@/pages/sender/scenario-meaning';
import {
	formatBulkSpamOutcome,
	senderSessionsExportFileName,
	shouldOpenConsoleAfterBulkSpam,
} from '@/pages/sender/sessions-actions';
import { SenderActionBar, SenderActionBarButton } from '@/pages/sender/components/sender-action-bar';
import SenderEmptyState from '@/pages/sender/components/sender-empty-state';
import SenderLoadError from '@/pages/sender/components/sender-load-error';
import SenderSelectRow from '@/pages/sender/components/sender-select-row';
import SenderSessionListItem from '@/pages/sender/sender-session-list-item';
import {
	formatAccountsDeletePrompt,
	mergePanelSenderSessions,
	PANEL_SENDER_SESSIONS_BULK_MAX,
	selectedIncludesArchived,
	selectedNonArchivedIds,
	selectionOverBulkLimit,
	sessionMatchesQuery,
	visibleIdsForSelectAll,
} from '@/pages/sender/sender-session-display';
import { SenderScreen, senderPrimaryButtonClassName } from '@/pages/sender/sender-screen';
import { useUnit } from 'effector-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

export default function SenderSessionsPage() {
	const navigate = useNavigate();
	const { senderPresetsEnabled } = useUnit($features);
	const [query, setQuery] = useState('');
	const [sessions, setSessions] = useState<PanelSenderSessionRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [selecting, setSelecting] = useState(false);
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [presets, setPresets] = useState<SenderRunPreset[]>([]);
	const [presetId, setPresetId] = useState<string | null>(null);
	const [scenarioOpen, setScenarioOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [busy, setBusy] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		setError(false);
		try {
			const [readyItems, archivedItems] = await Promise.all([
				fetchPanelSenderSessions({ status: 'ready', limit: 100 }),
				fetchPanelSenderSessions({ status: 'archived', limit: 100 }),
			]);
			setSessions(mergePanelSenderSessions(readyItems, archivedItems));
			setSelected(new Set());
		} catch {
			setError(true);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void load();
	}, [load]);

	useEffect(() => {
		if (!senderPresetsEnabled) {
			setPresets([]);
			setPresetId(null);
			return;
		}
		void fetchSenderPresets()
			.then(items => {
				setPresets(items);
				setPresetId(items[0]?.id ?? null);
			})
			.catch(() => setPresets([]));
	}, [senderPresetsEnabled]);

	const visible = useMemo(() => {
		const q = query.trim();
		if (!q) return sessions;
		return sessions.filter(session => sessionMatchesQuery(session, q));
	}, [sessions, query]);

	const selectedIds = [...selected];
	const hasArchivedSelected = selectedIncludesArchived(sessions, selectedIds);
	const archiveTargetIds = selectedNonArchivedIds(sessions, selectedIds);
	const overBulkLimit = selectionOverBulkLimit(selectedIds.length);

	function toggle(id: string) {
		setSelected(prev => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
				return next;
			}
			if (next.size >= PANEL_SENDER_SESSIONS_BULK_MAX) {
				toast.message('Не больше 100 за раз');
				return prev;
			}
			next.add(id);
			return next;
		});
	}

	function selectAllVisible() {
		const { ids, capped } = visibleIdsForSelectAll(visible);
		setSelected(new Set(ids));
		if (capped) {
			toast.message(`Отмечено ${PANEL_SENDER_SESSIONS_BULK_MAX} из ${visible.length} — лимит за раз`);
		}
	}

	async function download(blob: Blob, fileName: string) {
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = fileName;
		a.click();
		URL.revokeObjectURL(url);
	}

	async function handleExport() {
		if (selectedIds.length === 0) return;
		if (overBulkLimit) {
			toast.message(`Не больше ${PANEL_SENDER_SESSIONS_BULK_MAX} за раз`);
			return;
		}
		setBusy(true);
		try {
			const blob = await exportPanelSenderSessions({ sessionIds: selectedIds });
			await download(blob, senderSessionsExportFileName(selectedIds));
			toast.success('Файл скачан');
		} catch (error) {
			toast.error(resolveSenderActionError(error, 'Не удалось скачать'));
		} finally {
			setBusy(false);
		}
	}

	async function handleArchive() {
		if (archiveTargetIds.length === 0) return;
		setBusy(true);
		try {
			for (const id of archiveTargetIds) {
				await archivePanelSenderSession(id);
			}
			toast.success('В архиве');
			setSelecting(false);
			await load();
		} catch (error) {
			toast.error(resolveSenderActionError(error, 'Не удалось архивировать'));
		} finally {
			setBusy(false);
		}
	}

	async function handleDelete() {
		setBusy(true);
		try {
			for (const id of selectedIds) {
				await deleteSession(id);
			}
			toast.success('Аккаунты удалены');
			setDeleteOpen(false);
			setSelecting(false);
			await load();
		} catch (error) {
			toast.error(resolveSenderActionError(error, 'Не удалось удалить'));
		} finally {
			setBusy(false);
		}
	}

	async function handleLaunch() {
		if (!presetId || selectedIds.length === 0 || hasArchivedSelected || overBulkLimit) {
			if (overBulkLimit) {
				toast.message(`Не больше ${PANEL_SENDER_SESSIONS_BULK_MAX} за раз`);
			}
			return;
		}
		setBusy(true);
		try {
			const result = await bulkSpamPanelSessions({ sessionIds: selectedIds, presetId });
			const notice = formatBulkSpamOutcome(result);
			setScenarioOpen(false);
			if (shouldOpenConsoleAfterBulkSpam(result.created.length)) {
				toast.success(notice);
				navigate(senderRoutes.console, { state: { from: senderRoutes.accounts } });
			} else {
				toast.warning(notice);
			}
		} catch (error) {
			toast.error(resolveSenderActionError(error, 'Не удалось запустить'));
		} finally {
			setBusy(false);
		}
	}

	const footer = selecting ? (
		<SenderActionBar>
			{senderPresetsEnabled ? (
				<SenderActionBarButton
					disabled={busy || selectedIds.length === 0 || hasArchivedSelected || overBulkLimit}
					onClick={() => setScenarioOpen(true)}
				>
					Запустить
				</SenderActionBarButton>
			) : null}
			{archiveTargetIds.length > 0 ? (
				<SenderActionBarButton disabled={busy} onClick={() => void handleArchive()}>
					В архив
				</SenderActionBarButton>
			) : null}
			<SenderActionBarButton
				disabled={busy || selectedIds.length === 0 || overBulkLimit}
				onClick={() => void handleExport()}
			>
				Скачать
			</SenderActionBarButton>
			<SenderActionBarButton variant='destructive' disabled={busy || selectedIds.length === 0} onClick={() => setDeleteOpen(true)}>
				Удалить
			</SenderActionBarButton>
		</SenderActionBar>
	) : null;

	return (
		<SenderScreen title='Сессии' footer={footer}>
			<Input
				value={query}
				onChange={event => setQuery(event.target.value)}
				placeholder='Номер, метка или username'
				className='min-h-11 text-[15px]'
			/>
			<div className='flex items-center justify-end'>
				<button
					type='button'
					className='min-h-11 shrink-0 text-[15px] text-primary'
					onClick={() => {
						setSelecting(value => !value);
						setSelected(new Set());
					}}
				>
					{selecting ? 'Готово' : 'Выбрать'}
				</button>
			</div>
			{selecting ? (
				<button
					type='button'
					className='min-h-11 self-start text-[15px] text-primary'
					onClick={() => selectAllVisible()}
				>
					Все
				</button>
			) : null}
			{loading ? (
				<div className='flex justify-center py-8'>
					<Spinner />
				</div>
			) : error ? (
				<SenderLoadError onRetry={() => void load()} />
			) : visible.length === 0 ? (
				<SenderEmptyState
					message={sessions.length === 0 ? 'Сессий нет' : 'Ничего не найдено'}
					action={
						sessions.length === 0 ? (
							<Button type='button' className={senderPrimaryButtonClassName()} onClick={() => navigate(senderHrefBatch())}>
								Добавить сессии
							</Button>
						) : undefined
					}
				/>
			) : (
				<ul className='flex flex-col gap-2'>
					{visible.map(session => (
						<li key={session.id}>
							<SenderSessionListItem
								session={session}
								selecting={selecting}
								selected={selected.has(session.id)}
								onToggle={() => toggle(session.id)}
							/>
						</li>
					))}
				</ul>
			)}
			<Dialog open={scenarioOpen} onOpenChange={setScenarioOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Сценарий</DialogTitle>
					</DialogHeader>
					<ul className='flex max-h-64 flex-col gap-2 overflow-y-auto'>
						{presets.map(preset => (
							<li key={preset.id}>
								<SenderSelectRow
									title={preset.name}
									meaning={formatScenarioMeaning(preset)}
									selected={preset.id === presetId}
									onSelect={() => setPresetId(preset.id)}
								/>
							</li>
						))}
					</ul>
					<DialogFooter className='flex-col gap-2 sm:flex-col'>
						<Button type='button' className='min-h-11' disabled={!presetId || busy} onClick={() => void handleLaunch()}>
							Запустить
						</Button>
						<Button type='button' variant='outline' className='min-h-11' onClick={() => setScenarioOpen(false)}>
							Отмена
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{formatAccountsDeletePrompt(selectedIds.length)}</DialogTitle>
					</DialogHeader>
					<DialogFooter className='flex-col gap-2 sm:flex-col'>
						<Button type='button' variant='destructive' className='min-h-11' disabled={busy} onClick={() => void handleDelete()}>
							Удалить
						</Button>
						<Button type='button' variant='outline' className='min-h-11' onClick={() => setDeleteOpen(false)}>
							Отмена
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</SenderScreen>
	);
}

function senderHrefBatch() {
	return `${senderRoutes.send}?mode=batch`;
}
